import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Truck, RefreshCcw, Shield, Scale, Mail, Phone, Download, FileText } from "lucide-react";

const PoliciesPage = () => {
  const [activeSection, setActiveSection] = useState('shipping');
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['shipping', 'returns', 'privacy', 'terms'].includes(hash)) {
      setActiveSection(hash);
    }
  }, [location.hash]);

  const sections = [
    { id: 'shipping', label: 'Shipping Policy', icon: Truck },
    { id: 'returns', label: 'Returns & Refunds', icon: RefreshCcw },
    { id: 'privacy', label: 'Privacy Policy', icon: Shield },
    { id: 'terms', label: 'Terms of Service', icon: Scale },
  ];

  const msdsDocuments = [
    { name: 'Black Magic MSDS', url: 'https://customer-assets.emergentagent.com/job_cutabove-store/artifacts/fubk1cmh_Black-Magic-1%20MSDS.pdf' },
    { name: 'Blue Moon Polishing Rouge MSDS', url: 'https://customer-assets.emergentagent.com/job_cutabove-store/artifacts/j1ewaiyh_Blue-Moon-Polishing-Rouge-HLM-301%20MSDS.pdf' },
    { name: 'White Lightning Rouge MSDS', url: 'https://customer-assets.emergentagent.com/job_cutabove-shop/artifacts/59srm4f9_White-Lightning-Rouge%20MSDS.pdf' },
    { name: 'Zephyr Pro 32 Cherry Wax MSDS', url: 'https://customer-assets.emergentagent.com/job_cutabove-shop/artifacts/ouxznrwm_Zephyr-Pro-32-Cherry-Wax%20MSDS.pdf' },
    { name: 'Zephyr Pro 39 Protectant MSDS', url: 'https://customer-assets.emergentagent.com/job_cutabove-shop/artifacts/mjf39ohd_Zephyr-Pro-39-Protectant%20MSDS.pdf' },
    { name: 'Zephyr Pro 60 SUDZ MSDS', url: 'https://customer-assets.emergentagent.com/job_cutabove-shop/artifacts/wxoxih3o_ZS-020_Pro_60_SUDZ%20MSDS.pdf' },
    { name: 'Zephyr Pro 49 Slick Shine MSDS', url: 'https://customer-assets.emergentagent.com/job_cutabove-shop/artifacts/gifhn9ai_ZS-023_Pro_49_Slick_Shine%20MSDS.pdf' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl tracking-wide mb-4 text-center">POLICIES</h1>
        <p className="text-gray-400 text-center mb-12">Everything you need to know about shopping with us</p>

        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex items-center gap-2 px-4 py-2 rounded-sm font-medium transition-all ${activeSection === section.id ? 'bg-[#D62828] text-white' : 'bg-[#121212] text-gray-400 hover:text-white border border-white/10'}`}>
                <Icon className="w-4 h-4" />{section.label}
              </button>
            );
          })}
        </div>

        <div className="bg-[#121212] border border-white/5 rounded-sm p-6 md:p-10">
          {activeSection === 'shipping' && (
            <div className="space-y-8" data-testid="shipping-policy">
              <div className="flex items-center gap-3 mb-6"><Truck className="w-8 h-8 text-[#D62828]" /><h2 className="font-heading text-3xl">SHIPPING POLICY</h2></div>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <div><h3 className="text-xl font-semibold text-white mb-3">Order Processing</h3><p>All orders are processed within 1 to 2 business days (excluding weekends and holidays) after receiving your order confirmation email.</p></div>
                <div><h3 className="text-xl font-semibold text-white mb-3">Domestic Shipping Rates and Estimates</h3><p>Shipping charges are calculated and displayed at checkout. We use FedEx Ground for most shipments.</p><div className="mt-4 p-4 bg-[#0A0A0A] rounded-sm border border-white/5"><p className="text-[#D4AF37] font-semibold">FREE SHIPPING on orders $100+</p></div></div>
                <div><h3 className="text-xl font-semibold text-white mb-3">International Shipping</h3><p>We offer international shipping to Canada. Your order may be subject to import duties and taxes levied once the shipment reaches your destination country.</p></div>
                <div><h3 className="text-xl font-semibold text-white mb-3">Missing Orders</h3>
                  <div className="mt-3 flex flex-col sm:flex-row gap-4">
                    <a href="mailto:sales@cutabovepolishshop.store" className="flex items-center gap-2 text-[#D62828] hover:text-[#D4AF37]"><Mail className="w-4 h-4" />sales@cutabovepolishshop.store</a>
                    <a href="tel:336-736-0621" className="flex items-center gap-2 text-[#D62828] hover:text-[#D4AF37]"><Phone className="w-4 h-4" />336-736-0621</a>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeSection === 'returns' && (
            <div className="space-y-8" data-testid="returns-policy">
              <div className="flex items-center gap-3 mb-6"><RefreshCcw className="w-8 h-8 text-[#D62828]" /><h2 className="font-heading text-3xl">RETURNS & REFUNDS</h2></div>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>We want you to be completely satisfied. Items may be returned within 30 days of delivery if unused and in original packaging.</p>
                <div><h3 className="text-xl font-semibold text-white mb-3">Non-Returnable Items</h3><ul className="list-disc list-inside space-y-2 ml-4"><li>Opened or used chemical products (polishes, compounds, waxes)</li><li>Hazardous materials or aerosols that have been opened</li><li>Custom-ordered or personalized items</li></ul></div>
                <div><h3 className="text-xl font-semibold text-white mb-3">Refunds</h3><p>Approved refunds will be issued to your original payment method within 7-10 business days.</p></div>
              </div>
            </div>
          )}
          {activeSection === 'privacy' && (
            <div className="space-y-8" data-testid="privacy-policy">
              <div className="flex items-center gap-3 mb-6"><Shield className="w-8 h-8 text-[#D62828]" /><h2 className="font-heading text-3xl">PRIVACY POLICY</h2></div>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>Cut Above Polishing and Detailing is committed to protecting your privacy. We collect personal information, order data, and automatically collected data to process orders and improve our service.</p>
                <div><h3 className="text-xl font-semibold text-white mb-3">Data Security</h3><p>We implement reasonable security measures. Payment information is processed securely via Stripe and PayPal — we do not store full credit card details.</p></div>
                <p className="text-sm text-gray-500 mt-8">Effective Date: January 2, 2026</p>
              </div>
            </div>
          )}
          {activeSection === 'terms' && (
            <div className="space-y-8" data-testid="terms-policy">
              <div className="flex items-center gap-3 mb-6"><Scale className="w-8 h-8 text-[#D62828]" /><h2 className="font-heading text-3xl">TERMS OF SERVICE</h2></div>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>These Terms govern your access to and use of the Cut Above Polishing and Detailing website and online store. By using the Service, you agree to be bound by these Terms.</p>
                <div><h3 className="text-xl font-semibold text-white mb-3">Orders and Payments</h3><ul className="list-disc list-inside space-y-2 ml-4"><li>All orders are subject to acceptance and availability.</li><li>Prices may change without notice.</li><li>We use third-party payment processors and do not store full payment card details.</li></ul></div>
                <p className="text-sm text-gray-500 mt-8">Effective Date: January 2, 2026</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 bg-[#121212] border border-white/5 rounded-sm p-6">
          <h3 className="font-heading text-2xl mb-4 flex items-center gap-2"><FileText className="w-6 h-6 text-[#D62828]" />SAFETY DATA SHEETS (MSDS)</h3>
          <p className="text-gray-400 mb-4">Download Material Safety Data Sheets for our chemical products:</p>
          <div className="flex flex-wrap gap-4">
            {msdsDocuments.map((doc) => (
              <a key={doc.name} href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 px-4 py-3 rounded-sm hover:border-[#D62828] transition-colors">
                <Download className="w-5 h-5 text-[#D62828]" /><span>{doc.name}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center text-gray-400">
          <p>For questions, contact us at: <a href="mailto:sales@cutabovepolishshop.store" className="text-[#D62828] hover:underline">sales@cutabovepolishshop.store</a></p>
        </div>
      </div>
    </div>
  );
};

export default PoliciesPage;
